'use server';

/**
 * @fileOverview تدفق المحادثة لتوليد توصيات مخصصة للعميل على الصفحة الرئيسية.
 * 
 * - getPersonalizedRecommendations - وظيفة تقرأ تاريخ تصفح المستخدم وتقدم نصائح وسيارات مقترحة.
 */

import { ai } from '@/ai/genkit';
import { z } from 'genkit';

const RecommendationsInputSchema = z.object({
    historyString: z.string().describe('سلسلة نصية تحتوي على آخر السيارات وقطع الغيار التي شاهدها المستخدم.'),
});

export type RecommendationsInput = z.infer<typeof RecommendationsInputSchema>;

const RecommendationsOutputSchema = z.object({
    greeting: z.string().describe('تحية مخصصة ومرحبة مبنية على اهتماماته الحالية.'),
    advice: z.string().describe('نصيحة ميكانيكية أو شرائية ذكية مبنية على ما كان يتصفحه.'),
    suggestedSearch: z.string().describe('اقتراح نصي للبحث (مثلاً: "تصفح سيارات السيدان الاقتصادية" أو "ابحث عن فحمات فرامل أصلية").'),
});

export type RecommendationsOutput = z.infer<typeof RecommendationsOutputSchema>;

const recommendationsPrompt = ai.definePrompt({
    name: 'personalizedRecommendationsPrompt',
    input: { schema: RecommendationsInputSchema },
    output: { schema: RecommendationsOutputSchema },
    prompt: `أنت مستشار السيارات الشخصي الذكي في منصة "سيارتي 360".
  العميل عاد للتو إلى الصفحة الرئيسية للمنصة.
  هذا هو سجل تصفحه الأخير (السيارات وقطع الغيار التي كان يشاهدها):
  
  {{{historyString}}}

  بناءً على هذا السجل، قم بتوليد:
  1. تحية مخصصة (مثلاً إذا كان يبحث عن تويوتا، رحب به كمهتم بسيارات تويوتا).
  2. نصيحة ذكية (مثلاً: إذا كان يبحث عن سيارات قديمة، انصحه بأهمية خدمة "فحص الجودة الذكي" قبل الشراء. إذا كان يبحث عن قطع استهلاكية كالمكابح، ذكره بأهمية تغيير الزيوت أيضاً).
  3. اقتراح بحث ذكي (جملة قصيرة تشجعه على استكشاف شيء متعلق باهتمامه).

  ملاحظة هامة:
  - إذا كان السجل فارغاً أو غير واضح، قدم تحية عامة ترحب بعودته وتشجعه على استكشاف المعرض وخدمات الذكاء الاصطناعي.
  - اكتب باللغة العربية الواضحة والودية.
  - صغ ردك في تنسيق JSON يتوافق بدقة مع مساحة المخرجات.`,
});

export async function getPersonalizedRecommendations(input: RecommendationsInput): Promise<RecommendationsOutput> {
    try {
        return await recommendationsFlow(input);
    } catch (error) {
        console.error("Gemini AI API Error (likely quota exceeded):", error);
        return {
            greeting: "مرحباً بك مجدداً في سيارتي 360!",
            advice: "اكتشف أحدث السيارات المضافة وقطع الغيار الأصلية في منصتنا المتكاملة.",
            suggestedSearch: "أحدث السيارات"
        };
    }
}

const recommendationsFlow = ai.defineFlow(
    {
        name: 'recommendationsFlow',
        inputSchema: RecommendationsInputSchema,
        outputSchema: RecommendationsOutputSchema,
    },
    async (input) => {
        const { output } = await recommendationsPrompt(input);
        return output!;
    }
);
